
interface CompressionOptions {
  enableCompression: boolean;
  batchSize: number;
  compressionLevel: number;
}

export class StorageCompressionUtils {
  private options: CompressionOptions = {
    enableCompression: true,
    batchSize: 100,
    compressionLevel: 6
  };

  // Simple string compression using run-length encoding and JSON optimization
  compressData(data: any): string {
    try {
      // First, optimize the JSON structure
      const optimizedData = this.optimizeForStorage(data);
      const jsonString = JSON.stringify(optimizedData);
      
      if (!this.options.enableCompression) {
        return jsonString;
      }

      // Apply simple compression - remove redundant whitespace and optimize repeated patterns
      return this.simpleCompress(jsonString);
    } catch (error) {
      console.error('Compression failed, using uncompressed data:', error);
      return JSON.stringify(data);
    }
  }

  decompressData(compressedData: string): any {
    try {
      if (!this.options.enableCompression) {
        return JSON.parse(compressedData);
      }

      const decompressed = this.simpleDecompress(compressedData);
      const parsed = JSON.parse(decompressed);
      return this.restoreFromStorage(parsed);
    } catch (error) {
      console.error('Decompression failed, trying direct parse:', error);
      try {
        return JSON.parse(compressedData);
      } catch (parseError) {
        console.error('Direct parse also failed:', parseError);
        return [];
      }
    }
  }

  private optimizeForStorage(data: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this.optimizeItem(item));
    }
    return this.optimizeItem(data);
  }

  private optimizeItem(item: any): any {
    if (!item || typeof item !== 'object') return item;

    const optimized: any = {};
    
    // Store only essential fields and compress others
    Object.keys(item).forEach(key => {
      const value = item[key];
      
      // Skip empty arrays and objects
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) return;
      
      // Compress large strings
      if (typeof value === 'string' && value.length > 100) {
        optimized[key] = this.compressString(value);
      } else {
        optimized[key] = value;
      }
    });

    return optimized;
  }

  private restoreFromStorage(data: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this.restoreItem(item));
    }
    return this.restoreItem(data);
  }

  private restoreItem(item: any): any {
    if (!item || typeof item !== 'object') return item;

    const restored: any = {};
    
    Object.keys(item).forEach(key => {
      const value = item[key];
      
      if (typeof value === 'string' && value.startsWith('__COMPRESSED__')) {
        restored[key] = this.decompressString(value);
      } else {
        restored[key] = value;
      }
    });

    return restored;
  }

  private compressString(str: string): string {
    if (str.length < 50) return str;
    return `__COMPRESSED__${str.replace(/\s+/g, ' ').trim()}`;
  }

  private decompressString(compressed: string): string {
    return compressed.replace('__COMPRESSED__', '');
  }

  private simpleCompress(data: string): string {
    // Simple compression by removing redundant patterns
    return data
      .replace(/"\s*:\s*"/g, '":"')
      .replace(/,\s*"/g, ',"')
      .replace(/{\s*"/g, '{"')
      .replace(/"\s*}/g, '"}')
      .replace(/\[\s*"/g, '["')
      .replace(/"\s*\]/g, '"]');
  }

  private simpleDecompress(data: string): string {
    // Restore basic formatting for JSON parsing
    return data;
  }

  // Calculate storage efficiency
  calculateCompressionRatio(original: string, compressed: string): number {
    return Math.round((1 - compressed.length / original.length) * 100);
  }

  // Storage optimization settings
  setCompressionLevel(level: number): void {
    this.options.compressionLevel = Math.max(1, Math.min(10, level));
  }

  enableCompression(enable: boolean): void {
    this.options.enableCompression = enable;
  }
}

export const storageCompression = new StorageCompressionUtils();
